/*
 * Backend comment-only rules are review findings, not replacements. These
 * helpers keep their shape explicit so the review UI can share one queue while
 * Office.js routes comments to Range.insertComment and changes to replacement.
 */

import type { DocComment, DocReviewFinding, DocSuggestion } from "../state/types";

export function isDocComment(
  finding: DocReviewFinding | null | undefined
): finding is DocComment & {
  findingType: "comment";
} {
  return finding?.findingType === "comment";
}

export function isDocSuggestion(
  finding: { findingType?: string } | null | undefined
): finding is DocSuggestion & { findingType: "change" } {
  return finding?.findingType !== "comment";
}

function getFindingPosition(finding: DocReviewFinding): number {
  if (isDocComment(finding)) {
    return finding.position ?? finding.positionInParagraph ?? finding.start ?? 0;
  }
  return finding.position ?? 0;
}

export function normalizeReviewFindings(
  changes: DocSuggestion[] = [],
  comments: DocComment[] = []
): DocReviewFinding[] {
  return [
    ...changes.map((change) => ({ ...change, findingType: "change" as const })),
    ...comments.map((comment) => ({ ...comment, findingType: "comment" as const })),
  ].sort((left, right) => {
    const leftParagraph = left.paragraphIndex ?? 0;
    const rightParagraph = right.paragraphIndex ?? 0;
    if (leftParagraph !== rightParagraph) return leftParagraph - rightParagraph;
    return getFindingPosition(left) - getFindingPosition(right);
  });
}

export function getCommentAnchorText(comment: DocComment): string {
  return String(comment.text || comment.contextText || "").trim();
}

export function getCommentBody(comment: DocComment): string {
  return String(comment.comment || comment.explanation || "").trim();
}

export function occurrenceIndexBefore(text: string, term: string, position: number): number {
  if (!term) return 0;
  const slice = text.substring(0, Math.max(0, position));
  let count = 0;
  let pos = slice.indexOf(term);
  while (pos !== -1) {
    count++;
    pos = slice.indexOf(term, pos + 1);
  }
  return count;
}

function getCommentPosition(comment: DocComment): number {
  return comment.position ?? comment.positionInParagraph ?? comment.start ?? 0;
}

function assertCommentsSupported(unsupportedMessage: string): void {
  if (!Office.context.requirements.isSetSupported("WordApi", "1.4")) {
    throw new Error(unsupportedMessage);
  }
}

type SearchResult = ReturnType<Word.Range["search"]>;

async function resolveCommentRange(
  context: Word.RequestContext,
  searchRoot: Word.Range | Word.Paragraph,
  comment: DocComment,
  originalText: string
): Promise<Word.Range | null> {
  const anchor = getCommentAnchorText(comment);
  if (!anchor) return null;

  const occurrenceIndex = occurrenceIndexBefore(originalText, anchor, getCommentPosition(comment));
  const searchResult: SearchResult = searchRoot.search(anchor, { matchCase: true });
  searchResult.load("items");
  await context.sync();

  if (searchResult.items.length > occurrenceIndex && occurrenceIndex >= 0) {
    return searchResult.items[occurrenceIndex];
  }

  if (searchResult.items.length > 0) {
    return searchResult.items[0];
  }

  return null;
}

export async function syncHighlightDocComment(
  comment: DocComment,
  originalParagraphTexts: Record<number, string>
): Promise<void> {
  try {
    await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();

      const paraIndex = (comment.paragraphIndex ?? 0) - 1;
      if (paraIndex < 0 || paraIndex >= paragraphs.items.length) return;

      const originalText =
        originalParagraphTexts[comment.paragraphIndex ?? 0] || comment.paragraphText || "";
      const range = await resolveCommentRange(
        context,
        paragraphs.items[paraIndex],
        comment,
        originalText
      );
      if (range) {
        range.select();
        await context.sync();
      }
    });
  } catch (err) {
    console.error("Comment highlight sync error:", err);
  }
}

export async function insertDocComment(
  comment: DocComment,
  originalParagraphTexts: Record<number, string>,
  unsupportedMessage: string
): Promise<void> {
  assertCommentsSupported(unsupportedMessage);

  await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    const paraIndex = (comment.paragraphIndex ?? 0) - 1;
    if (paraIndex < 0 || paraIndex >= paragraphs.items.length) return;

    const originalText =
      originalParagraphTexts[comment.paragraphIndex ?? 0] || comment.paragraphText || "";
    const range = await resolveCommentRange(
      context,
      paragraphs.items[paraIndex],
      comment,
      originalText
    );
    if (range) {
      range.insertComment(getCommentBody(comment));
      await context.sync();
    }
  });
}

export async function insertSelectionComment(
  comment: DocComment,
  originalSelectionText: string,
  unsupportedMessage: string
): Promise<void> {
  assertCommentsSupported(unsupportedMessage);

  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const range = await resolveCommentRange(context, selection, comment, originalSelectionText);
    if (range) {
      range.insertComment(getCommentBody(comment));
      await context.sync();
    }
  });
}
